import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';

const TermsOfService = ({ onClose }) => {
  // Lock body scroll when modal is open
  useEffect(() => {
    const scrollY = window.scrollY;
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.body.style.overflow = '';
      window.scrollTo(0, scrollY);
    };
  }, []);

  const handleClose = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onClose();
  };

  const modalContent = (
    <div 
      className="fixed inset-0 z-[99999]"
      style={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 99999
      }}
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60"
        onClick={handleClose}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.6)'
        }}
      />
      
      {/* Modal Container */}
      <div 
        className="absolute inset-0 overflow-y-auto overscroll-contain"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        <div className="min-h-full p-3 sm:p-4 flex items-start justify-center">
          <div 
            className="bg-white rounded-lg shadow-2xl w-full max-w-4xl relative my-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close X Button - Top Right */}
            <button
              type="button"
              onClick={handleClose}
              className="absolute top-2 right-2 sm:top-3 sm:right-3 z-10 w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-gray-800 hover:bg-gray-900 text-white transition-all text-xl sm:text-2xl font-bold shadow-lg"
              aria-label="Close"
            >
              ✕
            </button>

            {/* Header */}
            <div className="bg-wood-brown text-white px-4 sm:px-6 py-3 sm:py-4 rounded-t-lg">
              <h1 className="text-xl sm:text-2xl font-bold pr-12">Terms of Service</h1>
            </div>

            {/* Content */}
            <div className="px-4 sm:px-6 py-6 sm:py-8 max-w-none">
              <div className="text-center mb-6 sm:mb-8">
                <h2 className="text-xl sm:text-2xl font-bold text-wood-dark">Terms of Service</h2>
                <p className="text-gray-600">Easy Floor KC (easyfloorkc.com)</p>
                <p className="text-sm text-gray-500 mt-2">Effective date: January 6, 2026</p>
                <p className="text-sm text-gray-500">Last updated: January 6, 2026</p>
              </div>

              {/* Section 1 */}
              <section className="mb-6 sm:mb-8">
                <h3 className="text-lg sm:text-xl font-bold text-wood-dark mb-3 sm:mb-4">1) Acceptance of these Terms</h3>
                <p className="text-gray-700 text-sm sm:text-base">
                  By accessing or using easyfloorkc.com, you agree to these Terms of Service ("Terms"). If you do not agree, do not use the website.
                </p>
              </section>

              {/* Section 2 */}
              <section className="mb-6 sm:mb-8">
                <h3 className="text-lg sm:text-xl font-bold text-wood-dark mb-3 sm:mb-4">2) Services and estimates</h3>
                <p className="text-gray-700 mb-4 text-sm sm:text-base">
                  Easy Floor KC provides flooring-related services. Any website content is for general information only and may change.
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-2 sm:ml-4 text-sm sm:text-base">
                  <li>Quotes/estimates are not final offers unless confirmed in writing (for example, in a signed proposal/work order).</li>
                  <li>Final pricing may change due to jobsite conditions, change orders, materials availability, or scope updates.</li>
                </ul>
              </section>

              {/* Section 3 */}
              <section className="mb-6 sm:mb-8">
                <h3 className="text-lg sm:text-xl font-bold text-wood-dark mb-3 sm:mb-4">3) Scheduling, access, and customer responsibilities</h3>
                <p className="text-gray-700 mb-4 text-sm sm:text-base">If you schedule an onsite visit or job:</p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-2 sm:ml-4 text-sm sm:text-base">
                  <li>You agree to provide safe and reasonable access to the work area.</li>
                  <li>You are responsible for securing pets, valuables, and fragile items.</li>
                  <li>You agree to disclose known hazards (mold, asbestos, structural issues, etc.) before work begins.</li>
                </ul>
              </section>

              {/* Section 4 */}
              <section className="mb-6 sm:mb-8">
                <h3 className="text-lg sm:text-xl font-bold text-wood-dark mb-3 sm:mb-4">4) Payments, deposits, and change orders</h3>
                <p className="text-gray-700 mb-4 text-sm sm:text-base">Unless otherwise agreed in writing:</p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-2 sm:ml-4 text-sm sm:text-base">
                  <li>A deposit may be required to reserve scheduling or order materials.</li>
                  <li>Balances are due per the invoicing terms provided to you.</li>
                  <li>Additional work outside the agreed scope may require a written change order and additional charges.</li>
                </ul>
              </section>

              {/* Section 5 */}
              <section className="mb-6 sm:mb-8">
                <h3 className="text-lg sm:text-xl font-bold text-wood-dark mb-3 sm:mb-4">5) Cancellations and rescheduling</h3>
                <p className="text-gray-700 text-sm sm:text-base">
                  Appointments and work dates may be rescheduled based on availability. If you cancel with short notice, you may be responsible for non-refundable costs already incurred (for example, special-order materials), as permitted by law.
                </p>
              </section>

              {/* Section 6 */}
              <section className="mb-6 sm:mb-8">
                <h3 className="text-lg sm:text-xl font-bold text-wood-dark mb-3 sm:mb-4">6) Photos and portfolio use</h3>
                <p className="text-gray-700 text-sm sm:text-base">
                  We may request to take before/after photos of work for quality control or marketing. If you do not want photos used publicly, tell us in writing before the project begins.
                </p>
              </section>

              {/* Section 7 */}
              <section className="mb-6 sm:mb-8">
                <h3 className="text-lg sm:text-xl font-bold text-wood-dark mb-3 sm:mb-4">7) Communications (calls, emails, SMS)</h3>
                <p className="text-gray-700 text-sm sm:text-base">
                  By providing your contact information, you consent to receive communications from us about your inquiry, appointments, and services. Text messages may include opt-out instructions (Reply STOP). Message frequency varies; message and data rates may apply.
                </p>
              </section>

              {/* Section 8 */}
              <section className="mb-6 sm:mb-8">
                <h3 className="text-lg sm:text-xl font-bold text-wood-dark mb-3 sm:mb-4">8) Website use rules</h3>
                <p className="text-gray-700 mb-4 text-sm sm:text-base">You agree not to:</p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-2 sm:ml-4 text-sm sm:text-base">
                  <li>Use the website for unlawful purposes</li>
                  <li>Attempt to gain unauthorized access to the site, servers, or systems</li>
                  <li>Interfere with normal operation (malware, scraping, hacking, etc.)</li>
                  <li>Post or transmit harmful or infringing content</li>
                </ul>
              </section>

              {/* Section 9 */}
              <section className="mb-6 sm:mb-8">
                <h3 className="text-lg sm:text-xl font-bold text-wood-dark mb-3 sm:mb-4">9) Intellectual property</h3>
                <p className="text-gray-700 text-sm sm:text-base">
                  All website content (text, graphics, logos, photos, and design) is owned by Easy Floor KC or its licensors and is protected by applicable laws. You may not reproduce or distribute website content without permission.
                </p>
              </section>

              {/* Section 10 */}
              <section className="mb-6 sm:mb-8">
                <h3 className="text-lg sm:text-xl font-bold text-wood-dark mb-3 sm:mb-4">10) Disclaimer of warranties</h3>
                <p className="text-gray-700 text-sm sm:text-base">
                  The website is provided on an "AS IS" and "AS AVAILABLE" basis. We disclaim warranties to the fullest extent permitted by law, including implied warranties of merchantability, fitness for a particular purpose, and non-infringement.
                </p>
              </section>

              {/* Section 11 */}
              <section className="mb-6 sm:mb-8">
                <h3 className="text-lg sm:text-xl font-bold text-wood-dark mb-3 sm:mb-4">11) Limitation of liability</h3>
                <p className="text-gray-700 text-sm sm:text-base">
                  To the fullest extent permitted by law, Easy Floor KC will not be liable for indirect, incidental, special, consequential, or punitive damages arising from your use of the website.
                </p>
              </section>

              {/* Section 12 */}
              <section className="mb-6 sm:mb-8">
                <h3 className="text-lg sm:text-xl font-bold text-wood-dark mb-3 sm:mb-4">12) Indemnification</h3>
                <p className="text-gray-700 text-sm sm:text-base">
                  You agree to indemnify and hold harmless Easy Floor KC from claims arising out of your misuse of the website or violation of these Terms.
                </p>
              </section>

              {/* Section 13 */}
              <section className="mb-6 sm:mb-8">
                <h3 className="text-lg sm:text-xl font-bold text-wood-dark mb-3 sm:mb-4">13) Governing law and venue</h3>
                <p className="text-gray-700 text-sm sm:text-base">
                  These Terms are governed by the laws of the state in which Easy Floor KC operates, without regard to conflict of laws principles. Any disputes will be brought in a court of competent jurisdiction in that area.
                </p>
              </section>

              {/* Section 14 */}
              <section className="mb-6 sm:mb-8">
                <h3 className="text-lg sm:text-xl font-bold text-wood-dark mb-3 sm:mb-4">14) Changes to these Terms</h3>
                <p className="text-gray-700 text-sm sm:text-base">
                  We may update these Terms at any time. Continued use of the website after changes means you accept the updated Terms.
                </p>
              </section>

              {/* Section 15 */}
              <section className="mb-6 sm:mb-8">
                <h3 className="text-lg sm:text-xl font-bold text-wood-dark mb-3 sm:mb-4">15) Contact</h3>
                <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
                  <p className="font-semibold text-gray-800 mb-2">Contact:</p>
                  <ul className="list-disc list-inside text-gray-700 space-y-1 text-sm sm:text-base">
                    <li>Website: easyfloorkc.com</li>
                    <li>Phone: (913) 538-8541</li>
                  </ul>
                </div>
              </section>
            </div>

            {/* Footer */}
            <div className="bg-gray-100 px-4 sm:px-6 py-3 sm:py-4 rounded-b-lg border-t">
              <button
                type="button"
                onClick={handleClose}
                className="w-full bg-wood-brown hover:bg-wood-dark text-white font-semibold py-3 px-6 rounded-lg transition-colors text-sm sm:text-base"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Use portal to render modal at document body level
  return ReactDOM.createPortal(modalContent, document.body);
};

export default TermsOfService;
